import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPersonalInfo, loadMarkdownContent } from '../utils/contentLoader';
const publicationsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/publications.json`);

import MarkdownContent from '../components/MarkdownContent';

function PublicationDetail({ isPhd }) {
  const { year, slug } = useParams();
  const [publication, setPublication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phdContent, setPhdContent] = useState('');
  const navigate = useNavigate();
  const personalInfo = getPersonalInfo();
  
  useEffect(() => {
    const loadPublication = async () => {
      let pub = null;
      
      if (isPhd) {
        // Find PhD thesis publication
        pub = publicationsData.publications.find(p => (p.type === 'PhD thesis' || p.type === 'PhD Thesis'));
        
        if (!pub) {
          console.error('No PhD thesis found in publications data');
          // Create a fallback publication if none is found
          pub = {
            id: "phd_thesis",
            title: "PhD Thesis",
            authorNames: [personalInfo.name || "Author"],
            type: "PhD Thesis",
            year: new Date().getFullYear(),
            abstract: "PhD thesis abstract will be displayed here."
          };
        }
        
        // Load PhD markdown content
        try {
          const content = await loadMarkdownContent('phd.md');
          setPhdContent(content);
        } catch (error) {
          console.error('Error loading PhD content:', error);
          setPhdContent('');
        }
      } else {
        // Find the publication based on the slug
        try {
          // Extract the ID from the slug (format: title__id)
          const idMatch = slug.match(/__(.+)$/);
          if (!idMatch) {
            console.error('Invalid slug format, no ID found');
            return;
          }
          
          const id = idMatch[1];
          pub = publicationsData.publications.find(p => p.id === id);
          
          if (!pub) {
            console.error(`Publication with ID ${id} not found`);
          }
        } catch (error) {
          console.error('Error finding publication:', error);
        }
      }
      
      setPublication(pub);
      setLoading(false);
      
      // Set page title
      if (pub) {
        document.title = `${personalInfo.name || 'Personal Website'} - ${pub.title}`;
      } else {
        document.title = `${personalInfo.name || 'Personal Website'} - Publication Not Found`;
      }
    };
    
    loadPublication();
    
    return () => {
      // Reset title when component unmounts
      document.title = personalInfo.name || 'Personal Website';
    };
  }, [slug, year, personalInfo.name, isPhd]);
  
  if (loading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner"></div>
        <span>Loading...</span>
      </div>
    );
  }
  
  if (!publication) {
    return (
      <div className="publication-detail">
        <div className="container">
          <h1>Publication Not Found</h1>
          <p>The requested publication could not be found.</p>
          <button onClick={() => navigate('/publications')} className="back-button">
            Back to Publications
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="publication-detail">
      <div className="container">
        <button onClick={() => navigate('/publications')} className="back-button">
          Back to Publications
        </button>
        
        <h1>
          {publication.title}
          {publication.isBestPaperAward && <span className="best-paper-badge">Best Paper Award</span>}
          {(publication.type === 'PhD thesis'||publication.type === 'PhD Thesis') && <span className="phd-thesis-badge">PhD Thesis</span>}
          {publication.type === 'Standard' && <span className="standard-badge">IEEE Standard</span>}
        </h1>
        
        <div className="publication-meta">
          <p className="publication-authors">
            {publication.authorNames?.join(', ') || 'Unknown Authors'}
          </p>
          
          <p className="publication-journal">
            {publication.journal && <span>{publication.journal}, </span>}
            {(publication.type === 'PhD thesis' || publication.type === 'PhD Thesis') && <span><strong>PhD thesis dissertation</strong>, University of Deusto, </span>}
            {publication.type === 'Standard' && <span><strong>IEEE Standard</strong>, </span>}
            {publication.year}
            {publication.volume && <span>, Vol. {publication.volume}</span>}
            {publication.issue && <span>, Issue {publication.issue}</span>}
            {publication.pages && <span>, pp. {publication.pages}</span>}
          </p>
          
          {publication.abstract && (
            <div className="publication-abstract">
              <h2>Abstract</h2>
              <p>{publication.abstract}</p>
            </div>
          )}
          
          <div className="publication-links">
            {publication.doi && (
              <a 
                href={`https://doi.org/${publication.doi}`}
                target="_blank"
                rel="noopener noreferrer"
                className="doi-link"
              >
                <i className="ai ai-doi"></i> {publication.doi}
              </a>
            )}
            
            {publication.url && (
              <a 
                href={publication.url}
                target="_blank"
                rel="noopener noreferrer"
                className="url-link"
              >
                <i className="fas fa-external-link-alt"></i> View Online
              </a>
            )}
            
            <a 
              href={`https://scholar.google.com/scholar?q=${encodeURIComponent(publication.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="scholar-link"
            >
              <i className="ai ai-google-scholar"></i> Google Scholar
            </a>
          </div>
          
          {/* Display PhD content if available */}
          {isPhd && phdContent && (
            <div className="phd-content">
              <MarkdownContent content={phdContent} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PublicationDetail;
